import { Dispatch, SetStateAction, useEffect, useState } from 'react';

/**
 * useState-shaped hook backed by sessionStorage. `key` should be stable for
 * the lifetime of the component — changing it mid-life writes the current
 * value to the new key and leaves the old key's data behind.
 */
export function useSessionStorageState<T>(
  key: string,
  initialValue: T
): [T, Dispatch<SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = sessionStorage.getItem(key);
      return raw === null ? initialValue : (JSON.parse(raw) as T);
    } catch {
      return initialValue;
    }
  });

  useEffect(() => {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* quota or serialization error — silently degrade to in-memory */
    }
  }, [key, value]);

  return [value, setValue];
}
